import type { Vec3 } from './types.ts';

export const EPSILON = 1e-10;

export function vec(x = 0, y = 0, z = 0): Vec3 {
  return { x, y, z };
}
export function add(a: Vec3, b: Vec3): Vec3 {
  return vec(a.x + b.x, a.y + b.y, a.z + b.z);
}
export function sub(a: Vec3, b: Vec3): Vec3 {
  return vec(a.x - b.x, a.y - b.y, a.z - b.z);
}
export function scale(a: Vec3, amount: number): Vec3 {
  return vec(a.x * amount, a.y * amount, a.z * amount);
}
export function dot(a: Vec3, b: Vec3): number {
  return a.x * b.x + a.y * b.y + a.z * b.z;
}
export function cross(a: Vec3, b: Vec3): Vec3 {
  return vec(a.y * b.z - a.z * b.y, a.z * b.x - a.x * b.z, a.x * b.y - a.y * b.x);
}
export function lengthSq(a: Vec3): number {
  return dot(a, a);
}
export function length(a: Vec3): number {
  return Math.sqrt(lengthSq(a));
}
export function distance(a: Vec3, b: Vec3): number {
  return length(sub(a, b));
}
export function normalize(a: Vec3): Vec3 {
  const magnitude = length(a);
  return magnitude > EPSILON && Number.isFinite(magnitude) ? scale(a, 1 / magnitude) : vec();
}
export function lerp(a: Vec3, b: Vec3, t: number): Vec3 {
  return add(scale(a, 1 - t), scale(b, t));
}
export function finite(a: Vec3): boolean {
  return Number.isFinite(a.x) && Number.isFinite(a.y) && Number.isFinite(a.z);
}
export function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}
export function projectPerpendicular(value: Vec3, axis: Vec3): Vec3 {
  return sub(value, scale(axis, dot(value, axis)));
}

export function rotateAroundAxis(value: Vec3, axisInput: Vec3, angle: number): Vec3 {
  const axis = normalize(axisInput);
  const cosine = Math.cos(angle);
  const sine = Math.sin(angle);
  return add(
    add(scale(value, cosine), scale(cross(axis, value), sine)),
    scale(axis, dot(axis, value) * (1 - cosine)),
  );
}

export function signedAngle(fromInput: Vec3, toInput: Vec3, axisInput: Vec3): number {
  const from = normalize(fromInput);
  const to = normalize(toInput);
  const axis = normalize(axisInput);
  return Math.atan2(dot(axis, cross(from, to)), clamp(dot(from, to), -1, 1));
}

export function orthogonalUnit(tangent: Vec3): Vec3 {
  const seed = Math.abs(tangent.y) < 0.9 ? vec(0, 1, 0) : vec(1, 0, 0);
  return normalize(projectPerpendicular(seed, tangent));
}

export function arrayVec(array: Float64Array, index: number): Vec3 {
  const offset = index * 3;
  return vec(array[offset], array[offset + 1], array[offset + 2]);
}

export function writeVec(array: Float64Array, index: number, value: Vec3): void {
  const offset = index * 3;
  array[offset] = value.x;
  array[offset + 1] = value.y;
  array[offset + 2] = value.z;
}
